// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a React hook for fetching customer data.
 * Sorting and filtering are performed server-side.
 */
import { CustomerFilter, getCustomers } from '@/customer/api/get-customers';
import { ONE_MINUTE_MS, TWO_MINUTES_MS } from '@/utilities/time-constants';
import { useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';

/**
 * Schema defining valid properties for sorting customer lists.
 * - 'name': Sort by customer name
 * - 'locations': Sort by number of locations
 * - 'assets-owned': Sort by total assets owned
 * - 'assets-on-location': Sort by assets currently at locations
 */
export const customerSortPropertySchema = z.enum([
  'name',
  'locations',
  'assets-owned',
  'assets-on-location',
]);

/**
 * Type definition for customer sort properties.
 * Inferred from the customerSortPropertySchema.
 */
export type CustomerSortProperty = z.infer<typeof customerSortPropertySchema>;

/**
 * Configuration options for the useCustomers hook.
 */
interface UseCustomersOptions {
  filter?: CustomerFilter;
}

/**
 * A React hook that requests a collection of customers from the remote API.
 * This hook suspends while requesting the customers.
 *
 * Sort and filter are sent as server-side parameters via the filter object.
 * Callers that omit page/pageSize receive all customers.
 *
 * @param options - Configuration for filtering customers
 * @returns Object containing customers array, total result count, and query metadata
 */
export function useCustomers(options: UseCustomersOptions = {}) {
  const { data, ...query } = useSuspenseQuery({
    queryKey: ['customer', options.filter],
    queryFn: () => getCustomers(options.filter),
    refetchOnWindowFocus: true,
    staleTime: ONE_MINUTE_MS,
    refetchInterval: TWO_MINUTES_MS,
  });

  return { customers: data.customers, resultCount: data.totalCount, ...query };
}
